import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  // Logo URLs
  const logoTransparent = 'https://storage.readdy-site.link/project_files/adf26630-c3d0-464a-8966-30fa50c96b96/c7428c2d-7200-443b-a1a8-138e797c9386_WhatsApp_Image_2025-07-02_at_11.__1_-removebg-preview.png?v=cd6c8ae28f57adf9667072260b372e46';
  const logoAr = 'https://storage.readdy-site.link/project_files/adf26630-c3d0-464a-8966-30fa50c96b96/0ecdd294-9b85-4d78-a1fe-a7ff33498ad8_unqcode.png?v=000ba96ae80d3e18dbcb8d7ee4f955cf';
  const logoEn = 'https://storage.readdy-site.link/project_files/adf26630-c3d0-464a-8966-30fa50c96b96/395614d0-b776-44db-a0ca-604166a657a6_unqcode_en.png?v=a713f08a4ff2afac805150c0dca1a29a';

  const scrolledLogo = i18n.language === 'ar' ? logoAr : logoEn;

  const navLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.services', path: '/services' },
    { labelKey: 'nav.revenueSharing', path: '/revenue-sharing' },
    { labelKey: 'nav.partnership', path: '/partnership' },
    { labelKey: 'nav.joinUs', path: '/join' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
            : 'bg-transparent'
        }`}
      >
        <div className="px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <div className="h-12 w-auto flex items-center">
                <img
                  src={scrolled ? scrolledLogo : logoTransparent}
                  alt="UniqueCode Logo"
                  className="h-10 w-auto object-contain transition-all duration-300"
                />
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg whitespace-nowrap ${
                    location.pathname === link.path
                      ? scrolled ? 'text-brand-red' : 'text-white'
                      : scrolled
                        ? 'text-gray-600 hover:text-brand-red hover:bg-gray-50'
                        : 'text-white/80 hover:text-white'
                  }`}
                >
                  {t(link.labelKey)}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-1 left-4 right-4 h-0.5 bg-brand-red rounded-full"></span>
                  )}
                </Link>
              ))}
            </div>

            {/* CTA + Language */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`text-sm font-medium px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  scrolled
                    ? 'border-gray-200 text-gray-600 hover:border-brand-red hover:text-brand-red'
                    : 'border-white/30 text-white/80 hover:border-white hover:text-white'
                }`}
              >
                <i className="ri-translate-2 text-sm"></i>
                {t('nav.switchLang')}
              </button>
              <Link to="/contact">
                <button className="px-5 py-2.5 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-bold rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                  {t('nav.getStarted')}
                </button>
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <div className="lg:hidden flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`p-2 rounded-lg cursor-pointer text-sm font-semibold transition-colors ${
                  scrolled ? 'text-gray-700 bg-gray-100' : 'text-white bg-white/10'
                }`}
              >
                {t('nav.switchLang')}
              </button>
              <button
                className={`p-2 rounded-lg cursor-pointer transition-colors ${
                  scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                <i className={`ri-${menuOpen ? 'close' : 'menu-3'}-line text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
          <div
            className={`absolute top-0 h-full w-72 bg-white shadow-2xl ${isRTL ? 'left-0' : 'right-0'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center">
                <img
                  src={scrolledLogo}
                  alt="UniqueCode Logo"
                  className="h-9 w-auto object-contain"
                />
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    location.pathname === link.path
                      ? 'bg-brand-red text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                  }`}
                >
                  {t(link.labelKey)}
                  <i className={`${isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'}`}></i>
                </Link>
              ))}
            </div>
            <div className="p-4 border-t border-gray-100 space-y-3">
              <button
                onClick={toggleLanguage}
                className="w-full py-3 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl cursor-pointer hover:border-brand-red hover:text-brand-red transition-all whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-translate-2 text-sm"></i>
                {t('nav.switchLang')}
              </button>
              <Link to="/contact">
                <button className="w-full py-3 bg-brand-red text-white text-sm font-bold rounded-xl cursor-pointer hover:bg-brand-red-dark transition-all whitespace-nowrap">
                  {t('nav.getStarted')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
